import { createFileRoute, Outlet } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { Organisation } from "@/lib/types";
import { ErrorState, Skeleton } from "@/ui/States";
import { Notice, Settle } from "@/ui/Notice";
import { Stack } from "@/ui/frame/Page";

export const Route = createFileRoute("/_app/o/$orgId")({
  component: OrganisationLayout,
});

export function organisationQuery(orgId: string) {
  return {
    queryKey: ["organisation", orgId] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      api.get<{ organisation: Organisation }>(`/organisations/${orgId}`, undefined, signal),
  };
}

/**
 * Everything inside one organisation.
 *
 * Its only visible job is the suspension notice, which has to be on every
 * screen rather than on the overview: somebody who follows a link straight to
 * a service and finds every control disabled needs to be told why there, not
 * one screen back.
 */
function OrganisationLayout() {
  const { orgId } = Route.useParams();
  const query = useQuery(organisationQuery(orgId));

  if (query.isLoading) {
    return (
      <Stack>
        <Skeleton className="h-7 w-56" />
        <Skeleton className="h-40 w-full" rounded="lg" />
      </Stack>
    );
  }

  if (query.isError) {
    return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  }

  const organisation = query.data!.organisation;

  return (
    <Stack>
      {organisation.status === "suspended" && (
        <Notice title="This organisation is suspended">
          {organisation.suspension_reason ||
            "Everything is read-only until it is reactivated. Contact support if you think this is a mistake."}
        </Notice>
      )}

      <Settle>
        <Outlet />
      </Settle>
    </Stack>
  );
}
